/**
 * The GM's side of the battle map.
 *
 * Everything here edits a draft. The players' screens do not move until
 * Publish, which is the point: a GM arranging an ambush, applying an AI
 * suggestion, or dragging an enemy to see how it looks should not be
 * broadcasting the experiment to four other people mid-fight.
 *
 * The exception is a player's own move, which arrives through polling and is
 * already on both boards by the time it gets here.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import type { BattleMap, MapToken, Seat } from '@/api/battlemap';
import {
  getBattleMap,
  publishMap,
  replaceSeat,
  resolveSuggestion,
  saveDraft,
  seatUrl,
} from '@/api/battlemap';
import { MapCanvas } from '@/components/MapCanvas';
import { userFacingError } from '@/api/client';

const POLL_MS = 2_500;

export function BattleMapBoard({
  map: initial,
  seats: initialSeats,
  onClose,
  presentation,
}: {
  map: BattleMap;
  seats: Seat[];
  onClose: () => void;
  /** Frame-driven tutorial state. No polling or writes in this mode. */
  presentation?: {
    selectedId?: string; view?: 'gm' | 'player'; copiedSeatId?: string;
    saving?: boolean; publishing?: boolean;
  };
}) {
  const [localMap, setMap] = useState<BattleMap>(initial);
  const map = presentation ? initial : localMap;
  const [seats, setSeats] = useState<Seat[]>(initialSeats);
  const [localSelected, setSelected] = useState<MapToken | null>(null);
  const selected = presentation ? map.draft_json.tokens.find(t => t.id === presentation.selectedId) ?? null : localSelected;
  const [tagDraft, setTagDraft] = useState('');
  const [localCopied, setCopied] = useState<string | null>(null);
  const copied = presentation?.copiedSeatId ?? localCopied;
  const [localPreview, setPreview] = useState(false);
  const preview = presentation ? presentation.view === 'player' : localPreview;
  const [refreshError, setRefreshError] = useState(false);
  const [replacing, setReplacing] = useState<string | null>(null);
  const refreshPending = useRef(false);
  const editGeneration = useRef(0);
  const [localSaving, setSaving] = useState(false);
  const saving = presentation?.saving ?? localSaving;
  const [localPublishing, setPublishing] = useState(false);
  const publishing = presentation?.publishing ?? localPublishing;
  const [saveError, setSaveError] = useState<string | null>(null);
  // A poll must not overwrite the drag the GM is in the middle of.
  const holdUntil = useRef(0);
  const pendingSaves = useRef(0);
  const saveHealthy = useRef(true);
  const saveQueue = useRef<Promise<void>>(Promise.resolve());
  const mapRef = useRef(initial);

  const state = map.draft_json;
  const dirty = map.revision !== map.published_revision;
  const editingBlocked =
    saving || publishing || (refreshError && !saveHealthy.current);

  const refresh = useCallback(
    async (force = false) => {
      if (
        !force &&
        (refreshPending.current ||
          pendingSaves.current > 0 ||
          Date.now() < holdUntil.current ||
          document.hidden)
      )
        return;
      const generation = editGeneration.current;
      refreshPending.current = true;
      try {
        const { map: latest, seats: latestSeats } = await getBattleMap(map.id);
        if (
          generation !== editGeneration.current ||
          (!force && pendingSaves.current > 0)
        )
          return;
        setRefreshError(false);
        if (force) saveHealthy.current = true;
        setMap((current) => {
          // Suggestions and player moves arrive by poll; a stale answer that
          // predates our own last write must not roll the board backwards.
          const next =
            force || latest.revision >= current.revision ? latest : current;
          mapRef.current = next;
          return next;
        });
        setSeats(latestSeats);
        return true;
      } catch {
        setRefreshError(true);
        return false;
      } finally {
        refreshPending.current = false;
      }
    },
    [map.id],
  );

  useEffect(() => {
    mapRef.current = map;
  }, [map]);

  useEffect(() => {
    if (presentation) return;
    const timer = window.setInterval(() => void refresh(), POLL_MS);
    const onVisible = () => {
      if (!document.hidden) void refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [refresh, presentation]);

  useEffect(() => {
    setSelected((current) =>
      current
        ? (map.draft_json.tokens.find((token) => token.id === current.id) ??
          null)
        : null,
    );
  }, [map.draft_json]);

  const persist = useCallback(
    (next: BattleMap['draft_json']) => {
      editGeneration.current += 1;
      if (pendingSaves.current === 0) saveHealthy.current = true;
      const expectedRevision = mapRef.current.revision;
      holdUntil.current = Date.now() + 3_000;
      const optimistic = {
        ...mapRef.current,
        draft_json: next,
        revision: expectedRevision + 1,
      };
      mapRef.current = optimistic;
      setMap(optimistic);
      pendingSaves.current += 1;
      setSaving(true);
      setSaveError(null);

      const task = saveQueue.current.then(async () => {
        try {
          // A previous queued save conflicted or failed. Every later document
          // was based on that unacknowledged state, so none of them is safe to
          // send; the finalizer below reloads server truth once the queue drains.
          if (!saveHealthy.current) return;
          const result = await saveDraft(map.id, next, expectedRevision);
          setMap((current) => {
            const acknowledged = {
              ...current,
              revision: Math.max(current.revision, result.revision),
            };
            mapRef.current = acknowledged;
            return acknowledged;
          });
        } catch (error) {
          saveHealthy.current = false;
          setSaveError(
            userFacingError(
              error,
              'That change wasn’t saved. The board has been restored; try again.',
            ),
          );
          throw error;
        } finally {
          pendingSaves.current -= 1;
          if (pendingSaves.current === 0) {
            if (!saveHealthy.current) {
              holdUntil.current = 0;
              const restored = await refresh(true);
              if (!restored)
                setSaveError(
                  'That change wasn’t saved. Reconnect to restore the board before editing.',
                );
            }
            setSaving(false);
          }
        }
      });
      // A rejected save must not poison the Promise chain. Later queued writes
      // see saveHealthy=false, skip their stale documents, and the final one
      // forces a reload before editing is enabled again.
      saveQueue.current = task.catch(() => undefined);
      return task;
    },
    [map.id, refresh],
  );

  function updateToken(id: string, patch: Partial<MapToken>) {
    if (presentation || publishing || (refreshError && !saveHealthy.current)) return;
    const currentState = mapRef.current.draft_json;
    const next = {
      ...currentState,
      tokens: currentState.tokens.map((t) =>
        t.id === id ? { ...t, ...patch } : t,
      ),
    };
    void persist(next).catch(() => undefined);
    if (selected?.id === id) setSelected({ ...selected, ...patch } as MapToken);
  }

  async function handlePublish() {
    if (presentation) return;
    if (pendingSaves.current || publishing || !saveHealthy.current) return;
    setPublishing(true);
    setSaveError(null);
    try {
      await saveQueue.current;
      if (!saveHealthy.current) return;
      await publishMap(map.id);
      await refresh(true);
    } catch (error) {
      setSaveError(
        userFacingError(
          error,
          'The board wasn’t published. Your draft is safe; please try again.',
        ),
      );
    } finally {
      setPublishing(false);
    }
  }

  async function handleSuggestion(id: string, apply: boolean) {
    if (presentation) return;
    if (pendingSaves.current || publishing) return;
    editGeneration.current += 1;
    setSaving(true);
    try {
      const { map: updated } = await resolveSuggestion(map.id, id, apply);
      mapRef.current = updated;
      setMap(updated);
    } catch (error) {
      setSaveError(
        userFacingError(
          error,
          'That suggestion couldn’t be updated. Refresh the board and try again.',
        ),
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleReplace(seatId: string) {
    if (presentation) return;
    if (replacing) return;
    setReplacing(seatId);
    try {
      const { seat: replacement } = await replaceSeat(map.id, seatId);
      setSeats((current) =>
        current.map((seat) => (seat.id === seatId ? replacement : seat)),
      );
    } catch (error) {
      setSaveError(
        userFacingError(
          error,
          'The player link couldn’t be replaced. Please try again.',
        ),
      );
    } finally {
      setReplacing(null);
    }
  }

  return (
    <section className="bmap">
      <header className="bmap__head">
        <div className="bmap__heading">
          <p className="desk-eyebrow">Shared combat board</p>
          <h2 className="card__title">Battle map</h2>
        </div>
        <div className="bmap__actions">
          <button
            type="button"
            className={dirty ? 'button button--primary' : 'button'}
            onClick={() => void handlePublish()}
            disabled={!dirty || saving || publishing || !saveHealthy.current}
          >
            {saving
              ? 'Saving…'
              : publishing
                ? 'Publishing…'
                : dirty
                  ? 'Publish to players'
                  : 'Published'}
          </button>
          <button
            type="button"
            className="button button--ghost"
            onClick={onClose}
          >
            Back to encounter
          </button>
        </div>
      </header>

      {map.suggestions.length ? (
        <div className="bmap__suggestions">
          {map.suggestions.map((s) => (
            <div key={s.id} className="bmap__suggestion">
              <span>{s.summary}</span>
              <div className="bmap__suggestion-actions">
                <button
                  type="button"
                  className="button button--small"
                  onClick={() => void handleSuggestion(s.id, true)}
                  disabled={saving}
                >
                  Apply
                </button>
                <button
                  type="button"
                  className="button button--ghost button--small"
                  onClick={() => void handleSuggestion(s.id, false)}
                  disabled={saving}
                >
                  Dismiss
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      <div className="bmap__toolbar">
        <div className="segmented" role="group" aria-label="Map view">
          <button
            type="button"
            className={
              !preview ? 'segmented__item is-active' : 'segmented__item'
            }
            aria-pressed={!preview}
            onClick={() => setPreview(false)}
          >
            GM draft
          </button>
          <button
            type="button"
            className={
              preview ? 'segmented__item is-active' : 'segmented__item'
            }
            aria-pressed={preview}
            onClick={() => setPreview(true)}
          >
            Players see
          </button>
        </div>
        <span className="bmap__visibility">
          {preview
            ? 'Published board · read only'
            : 'Edits stay private until published'}
        </span>
      </div>
      <p className="bmap__status" role="status">
        {saving
          ? 'Saving your draft…'
          : publishing
            ? 'Sharing the board…'
            : dirty
              ? map.published_json
                ? 'Unpublished changes. Your players still see the last board you published.'
                : 'Not published yet. Only you can see this board.'
              : 'Your players are seeing this board.'}
      </p>
      {refreshError ? (
        <div className="notice" role="status">
          Live updates paused. Reconnecting…{' '}
          <button
            type="button"
            className="button button--small"
            onClick={() => void refresh(true)}
          >
            Retry
          </button>
        </div>
      ) : null}
      {saveError ? (
        <div className="notice notice--error" role="alert">
          {saveError}
        </div>
      ) : null}
      <div className="bmap__layout">
        <div>
          {preview && !map.published_json ? (
            <div className="bmap__empty">
              <h3>The table is waiting</h3>
              <p>
                Publish your draft when you’re ready. Players will see the map
                here.
              </p>
            </div>
          ) : (
            <MapCanvas
              state={
                preview && map.published_json
                  ? {
                      ...map.published_json,
                      tokens: map.published_json.tokens.filter(
                        (t) => !t.invisible,
                      ),
                    }
                  : state
              }
              draggableIds={
                preview || editingBlocked ? [] : state.tokens.map((t) => t.id)
              }
              selectedId={preview ? null : (selected?.id ?? null)}
              onSelect={preview ? undefined : setSelected}
              onMove={(id, x, y) => updateToken(id, { x, y })}
            />
          )}
          {!preview ? (
            <p className="mapcanvas__help">
              Select a token to edit it. Drag or use arrow keys to move. Zoom in
              for precise moves.
            </p>
          ) : null}
          {(preview ? map.published_json?.notes : state.notes) ? (
            <p className="mapcanvas__help">
              {preview ? map.published_json?.notes : state.notes}
            </p>
          ) : null}
        </div>
        {!preview ? (
          <aside>
            <div className="bmap__roster" aria-label="Tokens">
              <h3>
                On the board{' '}
                <span className="desk-count">{state.tokens.length}</span>
              </h3>
              {state.tokens.map((token) => (
                <button
                  key={token.id}
                  type="button"
                  className="bmap__token"
                  aria-label={`Edit ${token.name}`}
                  aria-pressed={selected?.id === token.id}
                  onClick={() => {
                    setSelected(token);
                    setTagDraft('');
                  }}
                >
                  <span
                    className={`token-dot token-dot--${token.side}`}
                    aria-hidden="true"
                  />
                  <span>
                    {token.name}
                    <small>
                      {token.invisible
                        ? 'Hidden from players'
                        : token.side === 'opposition'
                          ? 'Opponent'
                          : token.side === 'party'
                            ? 'Player character'
                            : token.side === 'ally'
                              ? 'Ally'
                              : 'Neutral'}
                    </small>
                  </span>
                </button>
              ))}
            </div>
            {selected ? (
              <div className="bmap__inspector">
                <h3 className="bmap__inspector-title">{selected.name}</h3>
                <label className="bmap__toggle">
                  <input
                    type="checkbox"
                    checked={selected.invisible}
                    disabled={editingBlocked}
                    onChange={(e) =>
                      updateToken(selected.id, { invisible: e.target.checked })
                    }
                  />
                  Hidden from players
                </label>
                <div className="bmap__tags">
                  {selected.tags.map((tag) => (
                    <button
                      key={tag}
                      type="button"
                      className="bmap__tag"
                      title="Remove"
                      aria-label={`Remove ${tag}`}
                      disabled={editingBlocked}
                      onClick={() =>
                        updateToken(selected.id, {
                          tags: selected.tags.filter((t) => t !== tag),
                        })
                      }
                    >
                      {tag} ×
                    </button>
                  ))}
                </div>
                <form
                  className="bmap__tagadd"
                  onSubmit={(event) => {
                    event.preventDefault();
                    const tag = tagDraft.trim();
                    if (
                      !tag ||
                      saving ||
                      publishing ||
                      selected.tags.length >= 6
                    )
                      return;
                    updateToken(selected.id, {
                      tags: [...new Set([...selected.tags, tag])],
                    });
                    setTagDraft('');
                  }}
                >
                  <label className="field__hint" htmlFor="token-condition">
                    Add a condition
                  </label>
                  <input
                    id="token-condition"
                    className="field__input"
                    value={tagDraft}
                    maxLength={80}
                    placeholder="Prone, on fire…"
                    onChange={(e) => setTagDraft(e.target.value)}
                    disabled={editingBlocked || selected.tags.length >= 6}
                  />
                  <button
                    type="submit"
                    className="button button--small"
                    disabled={
                      !tagDraft.trim() ||
                      editingBlocked ||
                      selected.tags.length >= 6
                    }
                  >
                    Add condition
                  </button>
                  {selected.tags.length >= 6 ? (
                    <span className="field__hint">
                      Remove a condition to add another.
                    </span>
                  ) : null}
                </form>
              </div>
            ) : null}
          </aside>
        ) : null}
      </div>
      <details className="bmap__seats" open>
        <summary>
          Invite your players{' '}
          <span className="desk-count">{seats.length} links</span>
        </summary>
        <p className="field__hint">
          Send each player their own link. No account needed. They can move
          their character; your hidden tokens stay private.
        </p>
        {seats.length === 0 ? (
          <p className="field__hint">
            No active player links. Revoked links stay revoked when this map is
            reopened.
          </p>
        ) : (
          seats.map((seat) => (
            <div key={seat.id} className="bmap__seat">
              <span className="bmap__seat-label">{seat.label}</span>
              <button
                type="button"
                className="button button--small"
                onClick={() => {
                  if (presentation) return;
                  if (!navigator.clipboard) {
                    setSaveError(
                      'Your browser didn’t allow copying. Select the link and copy it manually.',
                    );
                    return;
                  }
                  void navigator.clipboard
                    .writeText(seatUrl(seat.key))
                    .then(() => {
                      setCopied(seat.id);
                      window.setTimeout(() => setCopied(null), 1500);
                    })
                    .catch(() =>
                      setSaveError(
                        'Your browser didn’t allow copying. Select the link and copy it manually.',
                      ),
                    );
                }}
              >
                {copied === seat.id ? 'Copied' : 'Copy link'}
              </button>
              <button
                type="button"
                className="button button--ghost button--small"
                onClick={() => void handleReplace(seat.id)}
                title="Invalidate this link and make a new one"
                disabled={replacing !== null}
              >
                Replace link
              </button>
              <span className="field__hint">
                {seat.last_seen_at
                  ? Date.now() - Date.parse(seat.last_seen_at) < 20_000
                    ? 'connected'
                    : 'away'
                  : 'not opened yet'}
              </span>
              <div className="bmap__seat-url">
                <input
                  className="field__input"
                  aria-label={`Player link for ${seat.label}`}
                  readOnly
                  value={presentation ? `https://app.throughline.gg/#/map/${seat.key}` : seatUrl(seat.key)}
                  onFocus={(e) => e.target.select()}
                />
              </div>
            </div>
          ))
        )}
      </details>
    </section>
  );
}
